/**
 * Viewer state shared by the View tab, the Study Explorer, the Inspector and
 * the QC dock panel.
 *
 * Deliberately *not* the home for the case manifest or the selected node: those
 * belong to `@/store/appStore` (`currentCase`, `selectedNodeId`) because the
 * ribbon, Pipeline and Results tabs read them too. This store holds only what
 * is meaningless outside the viewer — layout, display settings and the live
 * readings that come back from NiiVue.
 */

import { create } from "zustand";
import type { CaseManifest, CaseNode } from "@/ipc/contract";
import { useAppStore } from "@/store/appStore";
import type { LayoutMode, LocationInfo, MaskedIntensity, ViewPlane, VolumeStats } from "./types";
import { forEachInstance } from "./nvRegistry";

/** Which manifest array a node came from; drives the Study Explorer grouping. */
export type NodeGroup = "images" | "masks" | "transforms" | "atlases" | "measurements" | "reports";

/**
 * Group labels follow the mockup's tree (Images / Overlays / Derived / QC)
 * rather than the manifest's field names: "Overlays" is what a clinician calls
 * the mask array, and transforms are the only thing in the Derived branch that
 * the pipeline actually writes.
 */
export const NODE_GROUP_LABELS: Record<NodeGroup, string> = {
  images: "Images",
  masks: "Masks",
  transforms: "Transforms",
  atlases: "Atlases",
  measurements: "Measurements",
  reports: "Reports",
};

export const NODE_GROUP_ORDER: NodeGroup[] = [
  "images",
  "masks",
  "transforms",
  "atlases",
  "measurements",
  "reports",
];

export interface GroupedNode {
  group: NodeGroup;
  node: CaseNode;
}

/** Flattens the six manifest arrays, tagging each node with its group. */
export function groupedNodes(manifest: CaseManifest | null): GroupedNode[] {
  if (!manifest) return [];
  const groups: [NodeGroup, CaseNode[]][] = [
    ["images", manifest.images],
    ["masks", manifest.masks],
    ["transforms", manifest.transforms],
    ["atlases", manifest.atlases],
    ["measurements", manifest.measurements],
    ["reports", manifest.reports],
  ];
  return groups.flatMap(([group, nodes]) => nodes.map((node) => ({ group, node })));
}

export function nodesInGroup(manifest: CaseManifest | null, group: NodeGroup): CaseNode[] {
  return manifest ? manifest[group] : [];
}

export function findNode(manifest: CaseManifest | null, id: string | null): CaseNode | null {
  if (!id) return null;
  return groupedNodes(manifest).find((entry) => entry.node.id === id)?.node ?? null;
}

/**
 * A CaseNode stores a path relative to whichever root it came from, so the
 * absolute path the backend needs has to be reassembled from `origin`.
 */
export function nodeAbsPath(manifest: CaseManifest | null, node: CaseNode | null): string | null {
  if (!manifest || !node) return null;
  const root = node.origin === "input" ? manifest.inputRoot : manifest.outputRoot;
  if (!root) return null;
  return `${root.replace(/\/+$/, "")}/${node.relPath.replace(/^\/+/, "")}`;
}

interface ViewerState {
  // ---- what is loaded --------------------------------------------------
  baseNodeId: string | null;
  /** Checked overlays, in check order. Only the first is drawn today. */
  overlayNodeIds: string[];
  /** Second volume in `compare` layout. */
  compareNodeId: string | null;
  setBaseNode: (id: string) => void;
  setOverlayNodes: (ids: string[]) => void;
  toggleOverlayNode: (id: string) => void;

  // ---- layout ----------------------------------------------------------
  layout: LayoutMode;
  panes: ViewPlane[];
  setLayout: (layout: LayoutMode) => void;
  setPanePlane: (index: number, plane: ViewPlane) => void;
  /** The ribbon's Axial/Sagittal/Coronal buttons retarget the leading pane. */
  setPrimaryPlane: (plane: ViewPlane) => void;

  // ---- display ---------------------------------------------------------
  overlayVisible: boolean;
  overlayOpacity: number;
  baseColormap: string;
  overlayColormap: string;
  thresholdMin: number;
  thresholdMax: number;
  crosshair: boolean;
  linked: boolean;
  followCursor: boolean;
  showLabels: boolean;
  showScale: boolean;
  sliceThicknessMm: number;
  setOption: <K extends keyof ViewerState>(key: K, value: ViewerState[K]) => void;

  // ---- live readings ---------------------------------------------------
  location: LocationInfo | null;
  baseStats: VolumeStats | null;
  overlayStats: VolumeStats | null;
  maskedIntensity: MaskedIntensity | null;
  setLocation: (loc: LocationInfo | null) => void;
  setStats: (base: VolumeStats | null, overlay: VolumeStats | null, masked?: MaskedIntensity | null) => void;

  /** Moves the crosshair to an absolute voxel index along one image axis. */
  gotoSlice: (axis: 0 | 1 | 2, to: number) => void;
  stepSlice: (axis: 0 | 1 | 2, delta: number) => void;
}

const DEFAULT_PANES: ViewPlane[] = ["axial", "sagittal", "coronal", "render"];

export const useViewerStore = create<ViewerState>((set, get) => ({
  baseNodeId: null,
  overlayNodeIds: [],
  compareNodeId: null,
  setBaseNode: (id) =>
    set((state) => ({
      baseNodeId: id,
      ...(state.baseNodeId === id
        ? {}
        : { baseStats: null, maskedIntensity: null }),
    })),
  setOverlayNodes: (ids) =>
    set((state) => ({
      overlayNodeIds: ids,
      ...(state.overlayNodeIds[0] === ids[0]
        ? {}
        : { overlayStats: null, maskedIntensity: null }),
    })),
  toggleOverlayNode: (id) =>
    set((state) => {
      const overlayNodeIds = state.overlayNodeIds.includes(id)
        ? state.overlayNodeIds.filter((entry) => entry !== id)
        : [...state.overlayNodeIds, id];
      const firstChanged = (state.overlayNodeIds[0] ?? null) !== (overlayNodeIds[0] ?? null);
      return {
        overlayNodeIds,
        ...(firstChanged ? { overlayStats: null, maskedIntensity: null } : {}),
      };
    }),

  layout: "2x2",
  // Always four entries; the layout decides how many are shown, so switching
  // 2×2 → single → 2×2 restores whatever the user had configured.
  panes: DEFAULT_PANES,
  setLayout: (layout) => set({ layout }),
  setPanePlane: (index, plane) =>
    set((state) => {
      const panes = [...state.panes];
      panes[index] = plane;
      return { panes };
    }),
  setPrimaryPlane: (plane) =>
    set((state) => ({ panes: state.panes.map((existing, i) => (i === 0 ? plane : existing)) })),

  overlayVisible: true,
  overlayOpacity: 0.7,
  baseColormap: "gray",
  overlayColormap: "warm",
  thresholdMin: 0.2,
  thresholdMax: 1.0,
  crosshair: true,
  linked: true,
  followCursor: false,
  showLabels: true,
  showScale: true,
  // Display-only. NiiVue renders one voxel-thick slices; this value is carried
  // for the report and for a future MIP, and the Inspector says so.
  sliceThicknessMm: 1,
  setOption: (key, value) => set({ [key]: value } as unknown as Partial<ViewerState>),

  location: null,
  baseStats: null,
  overlayStats: null,
  maskedIntensity: null,
  // Every linked pane reports the same crosshair move, so drop repeats rather
  // than re-rendering the Inspector once per pane.
  setLocation: (location) => {
    const prev = get().location;
    if (
      prev &&
      location &&
      prev.vox[0] === location.vox[0] &&
      prev.vox[1] === location.vox[1] &&
      prev.vox[2] === location.vox[2]
    ) {
      return;
    }
    set({ location });
  },
  setStats: (baseStats, overlayStats, maskedIntensity = null) =>
    set({ baseStats, overlayStats, maskedIntensity }),

  gotoSlice: (axis, to) => {
    const current = get().location?.vox;
    if (!current) return;
    const delta: [number, number, number] = [0, 0, 0];
    delta[axis] = Math.round(to) - Math.round(current[axis]);
    if (delta[axis] === 0) return;
    forEachInstance((nv) => nv.moveCrosshairInVox(delta[0], delta[1], delta[2]));
  },

  stepSlice: (axis, delta) => {
    const step: [number, number, number] = [0, 0, 0];
    step[axis] = delta;
    forEachInstance((nv) => nv.moveCrosshairInVox(step[0], step[1], step[2]));
  },
}));

/** The manifest, read from the app store — the single source of truth. */
export function useManifest(): CaseManifest | null {
  return useAppStore((s) => s.currentCase);
}

export function useSelectedNode(): CaseNode | null {
  const manifest = useManifest();
  const id = useAppStore((s) => s.selectedNodeId);
  return findNode(manifest, id);
}
